// pages/CategoryPage.js - Complete Working with Cherry Theme & Stock Management
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../contexts/CartContext';
import { dbGet } from '../config/firebase';
import { 
  ArrowLeft, Folder, Package, ShoppingCart, Zap, Search, X,
  ChevronRight, Home, Flower2, Heart, Star, Truck, Clock,
  Filter, Grid, Gift, ChevronDown, Sliders, TrendingUp,
  Sparkles, Crown, Leaf, Coffee, Palette, Plus, Minus,
  ShoppingBag, Share2, AlertCircle, Info, CheckCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

// Custom Auto-close Centered Dialog Component
const CustomDialog = ({ isOpen, onClose, message, type, productName }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch(type) {
      case 'cart':
        return <ShoppingBag size={48} color="#dc2626" />;
      case 'wishlist':
        return <Heart size={48} color="#dc2626" fill="#dc2626" />;
      case 'share':
        return <Share2 size={48} color="#dc2626" />;
      case 'error':
        return <AlertCircle size={48} color="#ef4444" />;
      case 'info':
        return <Info size={48} color="#dc2626" />;
      default:
        return <Sparkles size={48} color="#dc2626" />;
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'cart':
        return 'Added to Cart!';
      case 'wishlist':
        return 'Wishlist Updated';
      case 'share':
        return 'Link Copied!';
      case 'error':
        return 'Oops!';
      case 'info':
        return 'Information';
      default:
        return 'Success!';
    }
  };

  return (
    <>
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease'
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '24px',
        zIndex: 2001,
        width: '90%',
        maxWidth: '340px',
        padding: '28px 20px',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.2s ease'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 16px',
          borderRadius: '50%',
          background: '#fff5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {getIcon()}
        </div>

        <h3 style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1a0a0a',
          marginBottom: '8px'
        }}>
          {getTitle()}
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#64748b',
          lineHeight: '1.5',
          marginBottom: productName ? '12px' : '0'
        }}>
          {message}
        </p>

        {productName && (
          <div style={{
            background: '#fff5f5',
            padding: '8px 12px',
            borderRadius: '12px',
            marginTop: '12px',
            border: '1px solid rgba(220,38,38,0.15)',
            display: 'inline-block'
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
              {productName}
            </span>
          </div>
        )}

        <div style={{
          width: '60px',
          height: '3px',
          background: '#e2e8f0',
          borderRadius: '3px',
          margin: '20px auto 0',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: '#dc2626',
            borderRadius: '3px',
            animation: 'shrink 2s linear forwards'
          }} />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </>
  );
};

// Subcategory Card (Folder Style) - Cherry Theme
const SubcategoryCard = ({ subcategory, productCount, image, onClick, isMobile }) => {
  if (!subcategory) return null;
  
  return (
    <div 
      onClick={() => onClick(subcategory)}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: isMobile ? '10px' : '14px',
        border: '1px solid rgba(220,38,38,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.1)';
        e.currentTarget.style.borderColor = '#dc2626';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
        e.currentTarget.style.borderColor = 'rgba(220,38,38,0.1)';
      }}
    >
      <div style={{
        width: isMobile ? '45px' : '55px',
        height: isMobile ? '45px' : '55px',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#fff5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {image ? (
          <img src={image} alt={subcategory.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Folder size={isMobile ? 22 : 26} color="#dc2626" />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 500, marginBottom: '4px', color: '#1a0a0a' }}>
          {subcategory.name}
        </h3>
        <p style={{ fontSize: '10px', color: '#dc2626' }}>{productCount} products</p>
      </div>
      <ChevronRight size={14} color="#dc2626" />
    </div>
  );
};

// Cherry Theme Product Card
const ProductCard = ({ product, onAddToCart, onBuyNow, isMobile }) => {
  const navigate = useNavigate();
  
  if (!product) return null;
  
  const discountPercent = product.oldPrice && product.oldPrice > product.price 
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) 
    : 0;
  const isOutOfStock = product.stock === false || product.stock === 'false' || product.outOfStock === true;
  const rating = product.rating || (3.5 + Math.random() * 1.5).toFixed(1);
  const reviewCount = Math.floor(Math.random() * 5000) + 100;

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
      toast.error('This product is out of stock');
      return;
    }
    onAddToCart(product, e);
  };

  const handleBuyClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
      toast.error('This product is out of stock');
      return;
    }
    onBuyNow(product, e);
  };

  if (isMobile) {
    return (
      <div 
        onClick={handleCardClick}
        style={{
          display: 'flex',
          background: 'white',
          borderRadius: '10px',
          overflow: 'hidden',
          border: isOutOfStock ? '1px solid #ef4444' : '1px solid rgba(220,38,38,0.1)',
          marginBottom: '10px',
          position: 'relative',
          cursor: 'pointer',
          opacity: isOutOfStock ? 0.6 : 1
        }}
      >
        <div style={{ width: '90px', minWidth: '90px', height: '90px', background: '#fefaf8', position: 'relative' }}>
          <img 
            src={product.image} 
            alt={product.name} 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'}
          />
          {discountPercent > 0 && !isOutOfStock && (
            <div style={{ position: 'absolute', top: '4px', left: '4px', background: '#dc2626', color: 'white', padding: '2px 6px', borderRadius: '2px', fontSize: '9px', fontWeight: 700 }}>
              {discountPercent}% OFF
            </div>
          )}
          {isOutOfStock && (
            <div style={{ position: 'absolute', top: '4px', left: '4px', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '2px', fontSize: '9px', fontWeight: 700 }}>
              Out of Stock
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: '8px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 500, margin: '0 0 4px', color: isOutOfStock ? '#94a3b8' : '#1a0a0a', lineHeight: '1.3' }}>
            {product.name}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <span style={{ background: '#388e3c', color: 'white', padding: '2px 4px', borderRadius: '3px', fontSize: '9px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
              {rating} <Star size={7} fill="white" />
            </span>
            <span style={{ fontSize: '9px', color: '#878787' }}>({reviewCount.toLocaleString()})</span>
          </div>
          <div style={{ marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>₹{product.price?.toLocaleString()}</span>
            {product.oldPrice && !isOutOfStock && (
              <span style={{ fontSize: '10px', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '6px' }}>₹{product.oldPrice?.toLocaleString()}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
            <button 
              onClick={handleAddClick}
              disabled={isOutOfStock}
              style={{ 
                flex: 1, 
                padding: '5px', 
                background: isOutOfStock ? '#e2e8f0' : '#ff9f00', 
                color: isOutOfStock ? '#94a3b8' : 'white', 
                border: 'none', 
                borderRadius: '4px', 
                fontSize: '9px', 
                fontWeight: 600, 
                cursor: isOutOfStock ? 'not-allowed' : 'pointer' 
              }}
            >
              Add
            </button>
            <button 
              onClick={handleBuyClick}
              disabled={isOutOfStock}
              style={{ 
                flex: 1, 
                padding: '5px', 
                background: isOutOfStock ? '#e2e8f0' : '#fb641b', 
                color: isOutOfStock ? '#94a3b8' : 'white', 
                border: 'none', 
                borderRadius: '4px', 
                fontSize: '9px', 
                fontWeight: 600, 
                cursor: isOutOfStock ? 'not-allowed' : 'pointer' 
              }}
            >
              Buy
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleCardClick}
      style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: isOutOfStock ? '1px solid #ef4444' : '1px solid rgba(220,38,38,0.1)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative',
        opacity: isOutOfStock ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.12)';
          e.currentTarget.style.borderColor = '#dc2626';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = isOutOfStock ? '#ef4444' : 'rgba(220,38,38,0.1)';
      }}
    >
      <div style={{ position: 'relative', paddingTop: '100%', background: '#fefaf8' }}>
        <img 
          src={product.image} 
          alt={product.name} 
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
          onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80'}
        />
        {discountPercent > 0 && !isOutOfStock && (
          <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#dc2626', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
            {discountPercent}% OFF
          </div>
        )}
        {isOutOfStock && (
          <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#ef4444', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
            Out of Stock
          </div>
        )}
        <button onClick={(e) => { e.stopPropagation(); }} style={{ 
          position: 'absolute', top: '8px', right: '8px', background: 'white', border: 'none', 
          borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', 
          justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
        }}>
          <Heart size={14} color="#999" />
        </button>
      </div>
      <div style={{ padding: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
          <span style={{ background: '#388e3c', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
            {rating} <Star size={10} fill="white" />
          </span>
          <span style={{ fontSize: '11px', color: '#878787' }}>({reviewCount.toLocaleString()})</span>
        </div>
        <h3 style={{ 
          fontSize: '13px', fontWeight: 500, margin: '0 0 4px', color: isOutOfStock ? '#94a3b8' : '#1a0a0a', 
          lineHeight: '1.3', overflow: 'hidden', display: '-webkit-box', 
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '34px' 
        }}>
          {product.name}
        </h3>
        <div style={{ marginBottom: '6px' }}>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#dc2626' }}>₹{product.price?.toLocaleString()}</span>
          {product.oldPrice && !isOutOfStock && (
            <span style={{ fontSize: '12px', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '8px' }}>
              ₹{product.oldPrice?.toLocaleString()}
            </span>
          )}
        </div>
        {discountPercent > 0 && !isOutOfStock && (
          <p style={{ fontSize: '10px', color: '#10b981', marginBottom: '8px' }}>
            Save ₹{(product.oldPrice - product.price).toLocaleString()}
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={handleAddClick}
            disabled={isOutOfStock}
            style={{ 
              flex: 1, 
              padding: '8px', 
              background: isOutOfStock ? '#e2e8f0' : '#ff9f00', 
              color: isOutOfStock ? '#94a3b8' : 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '11px', 
              fontWeight: 600, 
              cursor: isOutOfStock ? 'not-allowed' : 'pointer' 
            }}
          >
            Add to Cart
          </button>
          <button 
            onClick={handleBuyClick}
            disabled={isOutOfStock}
            style={{ 
              flex: 1, 
              padding: '8px', 
              background: isOutOfStock ? '#e2e8f0' : '#fb641b', 
              color: isOutOfStock ? '#94a3b8' : 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '11px', 
              fontWeight: 600, 
              cursor: isOutOfStock ? 'not-allowed' : 'pointer' 
            }}
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Main CategoryPage Component
const CategoryPage = () => {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { products: allProducts, loading } = useProducts();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [randomProducts, setRandomProducts] = useState([]);
  const [subcategoryImages, setSubcategoryImages] = useState({});
  const searchRef = useRef(null);
  
  // Recent searches state
  const [recentSearches, setRecentSearches] = useState(() => {
    try { 
      const saved = localStorage.getItem('recent_searches');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    message: '',
    type: 'cart',
    productName: null
  });

  const showDialog = (message, type = 'cart', productName = null) => {
    setDialog({ isOpen: true, message, type, productName });
  };

  const closeDialog = () => {
    setDialog({ isOpen: false, message: '', type: 'cart', productName: null });
  };

  // Get products array from the products object
  const getProductsArray = useCallback(() => {
    if (!allProducts) return [];
    if (Array.isArray(allProducts)) return allProducts;
    if (typeof allProducts === 'object') {
      const flatArray = [];
      Object.values(allProducts).forEach(category => {
        if (Array.isArray(category)) {
          flatArray.push(...category);
        }
      });
      return flatArray;
    }
    return [];
  }, [allProducts]);

  const productsArray = getProductsArray();
  
  // Filter only in-stock products for display
  const inStockProducts = useMemo(() => {
    return productsArray.filter(p => p && p.stock !== false && p.outOfStock !== true);
  }, [productsArray]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (selectedSubcategory) {
        setSelectedSubcategory(null);
        setSearchInput('');
        setSortBy('default');
        setPriceRange({ min: 0, max: 100000 });
      } else if (searchInput) {
        setSearchInput('');
        setShowSuggestions(false);
      } else {
        navigate(-1);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, selectedSubcategory, searchInput]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load category and subcategories
  useEffect(() => {
    const loadData = async () => {
      try {
        let cat = null;
        let subs = [];

        if (location.state?.category) {
          cat = location.state.category;
        } else {
          const savedCats = localStorage.getItem('shop_categories');
          if (savedCats) {
            const cats = JSON.parse(savedCats);
            cat = cats.find(c => c.id === categoryId);
          }
          if (!cat) {
            const fbCats = await dbGet('shop_categories');
            if (fbCats && Array.isArray(fbCats)) {
              cat = fbCats.find(c => c.id === categoryId);
            } else if (fbCats && typeof fbCats === 'object') {
              const catsArray = Object.values(fbCats);
              cat = catsArray.find(c => c.id === categoryId);
            }
          }
        }

        if (!cat) {
          cat = { id: categoryId, name: 'Category', icon: '🌸' };
        }
        setCategory(cat);

        const savedSubs = localStorage.getItem('shop_subcategories');
        if (savedSubs) {
          const allSubs = JSON.parse(savedSubs);
          subs = allSubs[cat.id] || [];
        } else {
          const fbSubs = await dbGet('shop_subcategories');
          if (fbSubs && fbSubs[cat.id]) subs = fbSubs[cat.id];
        }
        setSubcategories(subs);

        if (location.state?.selectedSubcategory) {
          setSelectedSubcategory(location.state.selectedSubcategory);
        }
      } catch (error) {
        console.error('Error loading category:', error);
      }
    };
    loadData();
  }, [categoryId, location]);

  // Load subcategory images
  useEffect(() => {
    if (inStockProducts.length > 0 && subcategories.length > 0) {
      const images = {};
      subcategories.forEach(sub => {
        const productsInSub = inStockProducts.filter(p => 
          p.subcategoryId === sub.id || p.subcategory === sub.name
        );
        if (productsInSub.length > 0) {
          const randomIndex = Math.floor(Math.random() * productsInSub.length);
          images[sub.id] = productsInSub[randomIndex]?.image;
        }
      });
      setSubcategoryImages(images);
    }
  }, [inStockProducts, subcategories]);

  // Filter and sort products
  useEffect(() => {
    if (!inStockProducts || !category) return;
    
    let filtered = [...inStockProducts];
    
    filtered = filtered.filter(p => 
      p.category === category.id || p.mainCategory === category.id
    );
    
    if (selectedSubcategory) {
      filtered = filtered.filter(p => 
        p.subcategoryId === selectedSubcategory.id || p.subcategory === selectedSubcategory.name
      );
    }
    
    if (searchInput.trim()) {
      const term = searchInput.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term) ||
        p.tag?.toLowerCase().includes(term)
      );
    }
    
    filtered = filtered.filter(p => 
      p.price >= priceRange.min && p.price <= priceRange.max
    );
    
    switch(sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'name-asc':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      default:
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    
    setProducts(filtered);
    setFilteredProducts(filtered);
  }, [inStockProducts, category, selectedSubcategory, sortBy, priceRange, searchInput]);

  // Get suggestions for search
  const suggestions = useMemo(() => {
    if (!searchInput.trim() || !inStockProducts) return [];
    const query = searchInput.toLowerCase();
    return inStockProducts
      .filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.tag?.toLowerCase().includes(query)
      )
      .slice(0, 5);
  }, [searchInput, inStockProducts]);

  const popularSearches = [
    { icon: TrendingUp, label: 'Birthday Flowers', query: 'birthday' },
    { icon: Heart, label: 'Red Roses', query: 'roses' },
    { icon: Crown, label: 'Luxury Bouquets', query: 'luxury' },
    { icon: Gift, label: 'Anniversary', query: 'anniversary' }
  ];

  const addToRecentSearches = (query) => {
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recent_searches', JSON.stringify(updated));
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recent_searches');
  };

  const handleSearchSubmit = (query = searchInput) => {
    if (query.trim()) {
      addToRecentSearches(query.trim());
      setSearchInput(query);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    addToRecentSearches(product.name);
    navigate(`/product/${product.id}`);
    setShowSuggestions(false);
    setSearchInput('');
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Random products for default view
  useEffect(() => {
    if (inStockProducts.length > 0 && !selectedSubcategory && !searchInput) {
      const shuffled = [...inStockProducts].sort(() => 0.5 - Math.random());
      setRandomProducts(shuffled.slice(0, 12));
    }
  }, [inStockProducts, selectedSubcategory, searchInput]);

  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      showDialog('Please login to add items to cart', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
      variantId: 'standard',
      variantName: 'Standard'
    });
    showDialog(`${product.name} added to cart!`, 'cart', product.name);
  };

  const handleBuyNow = (product, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      showDialog('Please login to continue', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    sessionStorage.setItem('buyNowProduct', JSON.stringify({
      items: [{ ...product, quantity: 1 }],
      totalAmount: product.price
    }));
    navigate('/checkout');
  };

  const handleSubcategoryClick = (sub) => {
    setSelectedSubcategory(sub);
    setSearchInput('');
    setSortBy('default');
    setPriceRange({ min: 0, max: 100000 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    if (selectedSubcategory) {
      setSelectedSubcategory(null);
      setSearchInput('');
      setSortBy('default');
      setPriceRange({ min: 0, max: 100000 });
    } else if (searchInput) {
      setSearchInput('');
      setShowSuggestions(false);
    } else {
      navigate(-1);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setShowSuggestions(false);
  };

  if (loading || !category) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  const displayProducts = (selectedSubcategory || searchInput) ? filteredProducts : randomProducts;

  return (
    <div style={{ minHeight: '100vh', background: '#fff5f5' }}>
      {/* Custom Dialog */}
      <CustomDialog 
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        message={dialog.message}
        type={dialog.type}
        productName={dialog.productName}
      />

      {/* Sticky Header - Cherry Theme */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#dc2626',
        padding: isMobile ? '10px 12px' : '12px 20px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <button 
            onClick={goBack} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: isMobile ? '6px 12px' : '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'white',
              fontWeight: 500,
              fontSize: isMobile ? '12px' : '13px'
            }}
          >
            <ArrowLeft size={isMobile ? 14 : 16} /> Back
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: isMobile ? '20px' : '24px' }}>{category.icon || '🌸'}</span>
            <h1 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 600, margin: 0, color: 'white' }}>
              {selectedSubcategory ? selectedSubcategory.name : category.name}
            </h1>
          </div>

          {!selectedSubcategory && !searchInput && (
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '11px', color: 'white' }}>{subcategories.length} Collections</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '10px' : '20px' }}>
        
        {/* Search Bar with Suggestions */}
        <div ref={searchRef} style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: isMobile ? '10px' : '12px', 
          marginBottom: '16px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          border: '1px solid rgba(220,38,38,0.1)',
          position: 'relative'
        }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
            <input
              type="text"
              placeholder="Search products in this category..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              style={{
                width: '100%',
                padding: '10px 40px 10px 38px',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            {searchInput && (
              <button 
                onClick={clearSearch} 
                style={{ 
                  position: 'absolute', 
                  right: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  color: '#94a3b8'
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Search Suggestions Dropdown */}
          {showSuggestions && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              zIndex: 1001,
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ padding: '12px' }}>
                {/* Recent Searches Section */}
                {recentSearches.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} color="#dc2626" />
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Recent Searches</span>
                      </div>
                      <button onClick={clearRecentSearches} style={{ fontSize: '10px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                        Clear All
                      </button>
                    </div>
                    {recentSearches.slice(0, 5).map((s, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => { setSearchInput(s); handleSearchSubmit(s); }} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px'
                        }}
                      >
                        <Clock size={14} color="#dc2626" />
                        <span style={{ fontSize: '13px', color: '#212121' }}>{s}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Popular Searches Section */}
                <div style={{ marginBottom: suggestions.length > 0 ? '16px' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: '0 8px' }}>
                    <TrendingUp size={14} color="#dc2626" />
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Popular Searches</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {popularSearches.map((item, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => { setSearchInput(item.query); handleSearchSubmit(item.query); }} 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: '#fff5f5',
                          border: '1px solid #ffe0e0',
                          borderRadius: '30px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        <item.icon size={14} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Product Suggestions Section */}
                {suggestions.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ padding: '0 8px', marginBottom: '8px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Products</div>
                    {suggestions.map((p, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => handleSuggestionClick(p)} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%',
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px'
                        }}
                      >
                        <img src={p.image} alt={p.name} style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }} />
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#212121' }}>{p.name}</div>
                          <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>₹{p.price?.toLocaleString()}</div>
                        </div>
                        <Search size={14} color="#dc2626" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      

        {/* Subcategories Grid */}
        {!selectedSubcategory && !searchInput && subcategories.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '3px', height: '18px', background: '#dc2626', borderRadius: '2px' }} />
              <p style={{ color: '#64748b', fontSize: '13px' }}>
                {subcategories.length} collections available
              </p>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
              gap: isMobile ? '10px' : '16px' 
            }}>
              {subcategories.map(sub => (
                <SubcategoryCard
                  key={sub.id}
                  subcategory={sub}
                  productCount={products.filter(p => p.subcategoryId === sub.id || p.subcategory === sub.name).length}
                  image={subcategoryImages[sub.id]}
                  onClick={handleSubcategoryClick}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div>
          {displayProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: isMobile ? '40px' : '60px', background: 'white', borderRadius: '16px', border: '1px solid rgba(220,38,38,0.1)' }}>
              <Package size={48} color="#cbd5e1" />
              <h3 style={{ marginTop: '16px', fontSize: '16px', color: '#1a0a0a' }}>No products found</h3>
              <p style={{ color: '#64748b', fontSize: '13px', marginTop: '8px' }}>
                {searchInput ? `No products matching "${searchInput}"` : 'No products available yet'}
              </p>
              {searchInput && (
                <button onClick={clearSearch} style={{ marginTop: '16px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <p style={{ color: '#64748b', fontSize: '12px' }}>
                  Showing {displayProducts.length} products
                  {selectedSubcategory && ` in ${selectedSubcategory.name}`}
                  {searchInput && ` matching "${searchInput}"`}
                </p>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', 
                gap: isMobile ? '12px' : '20px' 
              }}>
                {displayProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onBuyNow={handleBuyNow}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CategoryPage;